/**
 * Cloudflare Turnstile for human-only actions (Oct 21 2026).
 *
 * MediaBack gives cloud and datacenter IPs a small allowance on signup, showcase guest rooms, agent test calls, studio
 * trials and the site copilot (utils/humanFlowCloudGuard.js). With Turnstile on, the 429 it sends past that allowance
 * carries `challenge: "turnstile"`. A person on a VPN or office proxy then sees a quick check here, and the request is
 * retried once with the token in X-Turnstile-Token.
 *
 * Off unless REACT_APP_TURNSTILE_SITE_KEY is set at build time (the site key is public). Only same-site POSTs to the
 * guarded paths are ever retried, and only when their body can be sent again.
 */

const SITE_KEY = String(process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const TOKEN_HEADER = 'X-Turnstile-Token';

const GUARDED_PATHS = [
  /^\/api(\/graphql)?\/?$/,
  /^\/v1\/studio\/solution-capsules\/room-context\/?$/,
  /^\/v1\/custom-agents\/try-rooms\/?$/,
  /^\/v1\/ai-agents-studio\/sessions\/[^/]+\/(web-trial|phone-lease)\/?$/,
  /^\/api\/product-knowledge\/answer\/?$/,
];

export function isGuardedRequest(url, method, location = typeof window !== 'undefined' ? window.location : null) {
  if (String(method || 'GET').toUpperCase() !== 'POST') return false;
  let parsed;
  try {
    parsed = new URL(url, location ? location.href : 'https://mediasfu.com/');
  } catch (_) {
    return false;
  }
  const sameOrigin = location && parsed.origin === location.origin;
  const ours = parsed.protocol === 'https:' && (parsed.hostname === 'mediasfu.com' || parsed.hostname.endsWith('.mediasfu.com'));
  if (!sameOrigin && !ours) return false;
  return GUARDED_PATHS.some((pattern) => pattern.test(parsed.pathname));
}

let scriptPromise = null;
function loadTurnstileScript(doc) {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = doc.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile unavailable')));
      script.onerror = () => reject(new Error('Turnstile failed to load'));
      doc.head.appendChild(script);
    }).catch((error) => {
      // Every failed load must permit another attempt, including a load event
      // that arrives without the Turnstile API and synchronous DOM failures.
      scriptPromise = null;
      throw error;
    });
  }
  return scriptPromise;
}

/** Shows the check in a small dialog and resolves with a one-time token. Rejects if the person closes it. */
export function getTurnstileToken({ siteKey = SITE_KEY, doc = typeof document !== 'undefined' ? document : null } = {}) {
  if (!siteKey || !doc) return Promise.reject(new Error('Turnstile is not configured'));
  return loadTurnstileScript(doc).then((turnstile) => new Promise((resolve, reject) => {
    const overlay = doc.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'msfu-turnstile-title');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:grid;place-items:center;padding:16px;'
      + 'background:rgba(6,10,19,.72);font:15px/1.5 Inter,system-ui,sans-serif';
    const card = doc.createElement('div');
    card.style.cssText = 'width:100%;max-width:360px;background:#0b1220;border:1px solid #1a2740;border-radius:16px;'
      + 'padding:22px 20px;color:#c3cfdd;box-shadow:0 20px 60px rgba(0,0,0,.5)';
    const title = doc.createElement('h2');
    title.id = 'msfu-turnstile-title';
    title.textContent = 'Quick check';
    title.style.cssText = 'margin:0 0 6px;color:#e8eef6;font:600 19px/1.3 "Space Grotesk",Inter,system-ui,sans-serif';
    const text = doc.createElement('p');
    text.textContent = 'Please confirm you are a person to continue.';
    text.style.cssText = 'margin:0 0 16px';
    const widget = doc.createElement('div');
    widget.style.cssText = 'min-height:65px';
    const cancel = doc.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    cancel.style.cssText = 'margin-top:14px;min-height:38px;padding:0 16px;border-radius:10px;border:1px solid #1a2740;'
      + 'background:transparent;color:#e8eef6;font:inherit;cursor:pointer';
    card.append(title, text, widget, cancel);
    overlay.appendChild(card);
    doc.body.appendChild(overlay);

    let widgetId = null;
    let settled = false;
    const finish = (error, token) => {
      if (settled) return;
      settled = true;
      try { if (widgetId !== null) turnstile.remove(widgetId); } catch (_) { /* already gone */ }
      overlay.remove();
      if (error) reject(error); else resolve(token);
    };
    cancel.addEventListener('click', () => finish(new Error('Turnstile cancelled')));
    try {
      widgetId = turnstile.render(widget, {
        sitekey: siteKey,
        theme: 'dark',
        callback: (token) => finish(null, token),
        'error-callback': () => finish(new Error('Turnstile error')),
        'expired-callback': () => finish(new Error('Turnstile expired')),
      });
      // A callback can settle the promise before render returns its widget ID.
      if (settled) {
        try { if (widgetId !== null) turnstile.remove(widgetId); } catch (_) { /* already gone */ }
      } else {
        cancel.focus();
      }
    } catch (error) {
      finish(error);
    }
  }));
}

function withHeader(headers, name, value) {
  const out = {};
  if (headers && typeof headers.forEach === 'function' && !Array.isArray(headers)) {
    headers.forEach((v, k) => { out[k] = v; });
  } else if (Array.isArray(headers)) {
    headers.forEach(([k, v]) => { out[k] = v; });
  } else if (headers) {
    Object.assign(out, headers);
  }
  out[name] = value;
  return out;
}

/** Wraps window.fetch once so a Turnstile 429 on a guarded request is retried with a token. */
export function installTurnstileRetry({
  win = typeof window !== 'undefined' ? window : null,
  siteKey = SITE_KEY,
  getToken = getTurnstileToken,
} = {}) {
  if (!win || !siteKey || typeof win.fetch !== 'function' || win.__msfuTurnstileRetry) return false;
  if (win.navigator && win.navigator.userAgent === 'ReactSnap') return false;
  const original = win.fetch.bind(win);
  win.__msfuTurnstileRetry = true;
  win.fetch = async (input, init = {}) => {
    const response = await original(input, init);
    if (response.status !== 429) return response;
    const url = typeof input === 'string' ? input : (input && typeof input.href === 'string' ? input.href : null);
    if (!url || !isGuardedRequest(url, init.method, win.location)) return response;
    if (init.body != null && typeof init.body !== 'string') return response;
    let challenged = false;
    try {
      challenged = (await response.clone().json())?.challenge === 'turnstile';
    } catch (_) {
      challenged = false;
    }
    if (!challenged) return response;
    let token;
    try {
      token = await getToken({ siteKey });
    } catch (_) {
      return response;
    }
    return original(input, { ...init, headers: withHeader(init.headers, TOKEN_HEADER, token) });
  };
  return true;
}
